/**
 * Draining the queue.
 *
 * Two halves, kept apart: `drainOnce` decides what to send and what to do with the
 * answer, and is testable with a fake transport; `startSyncEngine` wires it to the
 * browser events that should trigger it, and is thin because it cannot be tested in
 * Node.
 *
 * The trigger set is deliberately belt-and-braces, because the platforms disagree:
 *
 *   Android  registers a background sync tag and drains with the tab closed.
 *   iOS      has never supported Background Sync and there is no shim. It gets
 *            foreground drains, a poll while visible, and a pagehide beacon.
 *
 * The real backstop for a record stranded on a phone is not on the phone at all: the
 * server notices the inspection is missing and tells the manager. A queue that
 * depends on the operator reopening the app is a queue that loses records.
 */

import { resolveOccurredAt } from '@tes-inspect/core/clock';

import type { InspectDb, OutboxRecord, PhotoRecord } from './idb-schema.ts';
import {
  dueSubmissions,
  markFailed,
  markPhotoUploaded,
  markSending,
  markSent,
  pendingCount,
  photosFor,
} from './outbox-store.ts';

export type SendResult =
  | { readonly ok: true; readonly serverId: number; readonly serverReceivedAtMs: number }
  | { readonly ok: false; readonly reason: string; readonly httpStatus?: number };

/**
 * The network boundary. Injected so the drain loop can be tested without one, and so
 * swapping how submissions travel touches a single implementation.
 */
export interface Transport {
  sendSubmission(record: OutboxRecord): Promise<SendResult>;
  sendPhoto(photo: PhotoRecord): Promise<SendResult>;
}

export interface DrainClock {
  readonly nowMs: number;
  readonly randomFraction: number;
}

export interface DrainSummary {
  readonly attempted: number;
  readonly sent: number;
  readonly stillPending: number;
}

export function systemDrainClock(): DrainClock {
  return { nowMs: Date.now(), randomFraction: Math.random() };
}

export async function drainOnce(
  db: InspectDb,
  transport: Transport,
  clock: DrainClock = systemDrainClock(),
): Promise<DrainSummary> {
  const due = await dueSubmissions(db, clock.nowMs);
  let sent = 0;

  for (const record of due) {
    if (await deliver(db, transport, { record, clock })) {
      sent += 1;
    }
  }

  return { attempted: due.length, sent, stillPending: await pendingCount(db) };
}

interface DeliveryInput {
  readonly record: OutboxRecord;
  readonly clock: DrainClock;
}

async function deliver(
  db: InspectDb,
  transport: Transport,
  input: DeliveryInput,
): Promise<boolean> {
  const { record, clock } = input;
  await markSending(db, record.id);

  const result = await transport.sendSubmission(record);

  if (!result.ok) {
    await markFailed(db, record.id, {
      reason: result.reason,
      httpStatus: result.httpStatus,
      nowMs: clock.nowMs,
      randomFraction: clock.randomFraction,
    });
    return false;
  }

  const resolved = resolveOccurredAt(record);

  await markSent(db, record.id, {
    outboxId: record.id,
    serverId: result.serverId,
    serverReceivedAtMs: result.serverReceivedAtMs,
    occurredAtMs: resolved.occurredAtMs,
    timeConfidence: resolved.confidence,
    kind: record.kind,
  });

  // Photos travel separately and never hold the record hostage. A 250KB image over a
  // failing LTE link fails an order of magnitude more often than a 1.5KB JSON body,
  // so the safety record ships first and the evidence catches up.
  await uploadPhotos(db, transport, record.id);
  return true;
}

async function uploadPhotos(db: InspectDb, transport: Transport, outboxId: string): Promise<void> {
  const photos = await photosFor(db, outboxId);

  for (const photo of photos) {
    if (photo.uploadStatus === 'pending' && (await transport.sendPhoto(photo)).ok) {
      await markPhotoUploaded(db, photo.id);
    }
  }
}
